/**
 * The Obligations tab of an entity (ALC-4765): the system catalog of fiscal obligations and the
 * accountant's decisions, by validity. Namespace `entityObligations`.
 */
export const entityObligationsPtBr = {
  intro:
    'Decida, por vigência, quais obrigações fiscais valem para esta empresa. O ERP não decide por você: enquanto não houver decisão, a obrigação fica pendente de enquadramento.',
  tableLabel: 'Obrigações fiscais da entidade',
  loadError: 'Não foi possível carregar as obrigações desta entidade.',
  empty: {
    title: 'Nenhuma obrigação no catálogo',
    description: 'O catálogo de obrigações do sistema ainda não foi carregado.',
  },
  pending_one: '{{count}} obrigação pendente de enquadramento',
  pending_other: '{{count}} obrigações pendentes de enquadramento',
  allDecided: 'Todas as obrigações têm decisão para esta data.',
  asOf: 'Situação em {{date}}',
  groups: {
    federal: 'Federais, decididas pelo contribuinte',
    municipal: 'Municipais, decididas por estabelecimento',
  },
  columns: {
    obligation: 'Obrigação',
    periodicity: 'Periodicidade',
    deadline: 'Prazo',
    status: 'Situação',
    since: 'Desde',
  },
  periodicity: {
    MONTHLY: 'Mensal',
    QUARTERLY: 'Trimestral',
    SEMIANNUAL: 'Semestral',
    ANNUAL: 'Anual',
    EVENT: 'Por evento',
  },
  deadline: {
    none: 'Sem prazo fixo',
    day: 'Dia {{day}}',
    dayAnticipated: 'Dia {{day}}, antecipado para o dia útil anterior',
    dayPostponed: 'Dia {{day}}, adiado para o próximo dia útil',
    businessDay: '{{day}}º dia útil',
    lastDay: 'Último dia',
    lastBusinessDay: 'Último dia útil',
    sameMonth: '{{rule}} do próprio mês',
    nextMonth: '{{rule}} do mês seguinte',
    monthsAfter: '{{rule}}, {{count}} meses depois',
    source: 'Fonte: {{source}} (regra {{version}})',
  },
  state: {
    APPLICABLE: 'Aplicável',
    WAIVED_WITH_BASIS: 'Dispensada com fundamento',
    PENDING_CLASSIFICATION: 'Pendente de enquadramento',
    OUT_OF_SCOPE_FLAGGED: 'Fora do escopo, sinalizada',
  },
  since: 'desde {{date}}',
  screeningOnly: 'O ERP ainda não cobre esta obrigação: só é possível sinalizá-la.',
  paymentWith: 'Pagamento: {{code}}',
  history: {
    title: 'Decisões por vigência',
    none: 'Nenhuma decisão registrada.',
    open: '{{from}} em diante',
    range: '{{from}} a {{to}}',
    reason: 'Motivo: {{reason}}',
    basis: 'Fundamento: {{basis}}',
    evidence: 'Evidência: {{evidence}}',
  },
  actions: {
    decide: 'Decidir',
    decideLabel: 'Decidir {{name}}',
    deadline: 'Prazo',
    deadlineLabel: 'Prazo de {{name}}',
    more: 'Ações de {{name}}',
  },
  deadlineRule: {
    column: 'Regra da empresa: {{rule}}',
    from: 'a partir de {{date}}',
    entry: '{{rule}}, {{from}}. Fonte: {{source}}',
    catalog: 'Regra do catálogo: {{rule}}. {{source}}',
  },
  deadlineDialog: {
    title: 'Prazo: {{name}}',
    description:
      'Registre a regra de prazo desta empresa quando ela difere do catálogo, por exemplo a regra do ISS do município. As ocorrências ainda não transmitidas passam a usar a nova regra, com a fonte registrada.',
    listTitle: 'Regras desta empresa',
    none: 'Nenhuma regra própria: vale o prazo do catálogo.',
    remove: 'Remover',
    removeLabel: 'Remover a regra {{from}}',
    removeError: 'Não foi possível remover a regra. Tente novamente.',
    readOnly: 'Somente leitura: as regras de prazo das obrigações federais são da matriz.',
    readOnlyNoCompanyAccess:
      'Somente leitura: para mudar esta regra, é preciso ter acesso a todos os estabelecimentos da empresa.',
    formTitle: 'Nova regra',
    validFrom: 'Vale para períodos que terminam a partir de',
    validFromHelp: 'Informe uma data a partir de 01/01/2027.',
    day: 'Dia do vencimento',
    dayLast: 'Último dia',
    dayOption: 'Dia {{day}}',
    businessDay: 'Contar em dias úteis',
    monthOffset: 'Meses após o fim do período',
    monthOffsetHelp: '0 vence no próprio mês do período; 1, no mês seguinte.',
    source: 'Fonte da regra',
    sourcePlaceholder: 'Por exemplo: Decreto municipal 123/2026, art. 4º',
    submit: 'Salvar prazo',
    close: 'Fechar',
    saveError: 'Não foi possível salvar o prazo. Tente novamente.',
  },
  readOnly: {
    notHeadOffice:
      'As obrigações federais são decididas na matriz. Aqui elas aparecem só para leitura.',
    openHeadOffice: 'Abrir a matriz',
    noTaxpayer:
      'Esta entidade não tem contribuinte federal. Informe o contribuinte em "Informações básicas" para decidir as obrigações federais; as municipais já podem ser decididas.',
    noPermission: 'Somente leitura: você não tem permissão para decidir obrigações.',
    row: 'Decidida na matriz',
    noCompanyAccess:
      'Para decidir as obrigações federais, é preciso ter acesso a todos os estabelecimentos da empresa. Peça a quem administra o cadastro da empresa.',
    rowNoCompanyAccess: 'Sem acesso a toda a empresa',
  },
  dialog: {
    title: 'Decidir: {{name}}',
    description:
      'A decisão vale a partir da data de início e fecha a anterior no dia anterior. As decisões antigas continuam no histórico.',
    stateLabel: 'Decisão',
    validFrom: 'Vale a partir de',
    validFromPlaceholder: 'dd/mm/aaaa',
    validFromHelp: 'As decisões valem a partir de 01/01/2027.',
    validFromError: 'Informe uma data válida, a partir de 01/01/2027.',
    validTo: 'Vale até',
    validToError: 'Informe uma data válida, igual ou posterior ao início.',
    basisCode: 'Código do fundamento',
    basisDescription: 'Fundamento legal',
    evidenceCode: 'Código da evidência',
    evidenceDescription: 'Evidência',
    reason: 'Motivo',
    reasonFlagHelp:
      'Explique por que o ERP não cobre esta obrigação. Ela aparece como lacuna na central de obrigações.',
    submit: 'Salvar decisão',
    cancel: 'Cancelar',
    saveError: 'Não foi possível salvar a decisão. Tente novamente.',
  },
  errors: {
    OBLIGATION_NOT_FOUND: 'Esta obrigação não está no catálogo. Atualize a página.',
    OBLIGATION_DATE_INVALID:
      'Informe datas válidas. A data final precisa ser igual ou posterior à inicial.',
    OBLIGATION_BEFORE_CALENDAR_START: 'As decisões valem a partir de 01/01/2027.',
    OBLIGATION_WAIVER_REQUIRES_BASIS_AND_EVIDENCE:
      'Para dispensar uma obrigação, informe o fundamento legal e a evidência, cada um com código e descrição.',
    OBLIGATION_OUT_OF_SCOPE_REQUIRES_REASON:
      'Para sinalizar a obrigação como fora do escopo, informe o motivo.',
    OBLIGATION_SCREENING_ONLY_CANNOT_APPLY:
      'O ERP ainda não cobre esta obrigação. Sinalize-a como fora do escopo, com o motivo.',
    OBLIGATION_SCOPE_MISMATCH:
      'Obrigações federais são decididas na matriz e as municipais por estabelecimento.',
    OBLIGATION_APPLICABILITY_PERIOD_LOCKED:
      'Uma obrigação já transmitida usa esta decisão. Escolha uma data de início posterior ao período entregue.',
    OBLIGATION_APPLICABILITY_OVERLAP:
      'Já existe uma decisão para esta obrigação no período. Atualize a página e tente novamente.',
    OBLIGATION_RETRY:
      'As decisões mudaram durante o salvamento. Atualize a página e tente novamente.',
    OBLIGATION_DEADLINE_RULE_INVALID:
      'Informe o dia do prazo (de 1 a 31, ou o último dia), os meses após o período (de 0 a 24) e a fonte da regra.',
    OBLIGATION_NO_PERIODS: 'Esta obrigação não tem períodos de apuração, então não tem prazo.',
    OBLIGATION_DEADLINE_OVERRIDE_NOT_FOUND:
      'Não encontramos esta regra de prazo. Atualize a página e tente novamente.',
    // The entity and Taxpayer rules are the shared establishment-profile ones.
    ESTABLISHMENT_PROFILE_ENTITY_NOT_FOUND: 'Não encontramos o estabelecimento. Atualize a página.',
    ESTABLISHMENT_PROFILE_TAXPAYER_NOT_FOUND:
      'Não encontramos o contribuinte federal. Atualize a página.',
    ESTABLISHMENT_PROFILE_TAXPAYER_REQUIRED:
      'Esta entidade não tem contribuinte federal. Informe o contribuinte em "Informações básicas".',
    ESTABLISHMENT_PROFILE_HEAD_OFFICE_ONLY:
      'Obrigações federais são decididas na matriz. Abra a matriz para alterar.',
    ESTABLISHMENT_PROFILE_ENTITY_SCOPE_FORBIDDEN:
      'Este estabelecimento está fora do seu escopo de acesso. Peça a um administrador para ampliar o acesso.',
  },
} as const;

type Widen<T> = T extends string ? string : { [K in keyof T]: Widen<T[K]> };

export const entityObligationsEn: Widen<typeof entityObligationsPtBr> = {
  intro:
    'Decide, by validity, which fiscal obligations apply to this company. The ERP does not decide for you: until there is a decision, the obligation stays pending classification.',
  tableLabel: 'Fiscal obligations of the entity',
  loadError: 'Could not load the obligations of this entity.',
  empty: {
    title: 'No obligations in the catalog',
    description: 'The system obligation catalog has not been loaded yet.',
  },
  pending_one: '{{count}} obligation pending classification',
  pending_other: '{{count}} obligations pending classification',
  allDecided: 'Every obligation has a decision for this date.',
  asOf: 'Status on {{date}}',
  groups: {
    federal: 'Federal, decided by the Taxpayer',
    municipal: 'Municipal, decided per establishment',
  },
  columns: {
    obligation: 'Obligation',
    periodicity: 'Frequency',
    deadline: 'Deadline',
    status: 'Status',
    since: 'Since',
  },
  periodicity: {
    MONTHLY: 'Monthly',
    QUARTERLY: 'Quarterly',
    SEMIANNUAL: 'Semiannual',
    ANNUAL: 'Annual',
    EVENT: 'Per event',
  },
  deadline: {
    none: 'No fixed deadline',
    day: 'Day {{day}}',
    dayAnticipated: 'Day {{day}}, moved back to the previous business day',
    dayPostponed: 'Day {{day}}, moved to the next business day',
    businessDay: 'Business day {{day}}',
    lastDay: 'Last day',
    lastBusinessDay: 'Last business day',
    sameMonth: '{{rule}} of the same month',
    nextMonth: '{{rule}} of the following month',
    monthsAfter: '{{rule}}, {{count}} months later',
    source: 'Source: {{source}} (rule {{version}})',
  },
  state: {
    APPLICABLE: 'Applicable',
    WAIVED_WITH_BASIS: 'Waived with basis',
    PENDING_CLASSIFICATION: 'Pending classification',
    OUT_OF_SCOPE_FLAGGED: 'Out of scope, flagged',
  },
  since: 'since {{date}}',
  screeningOnly: 'The ERP does not cover this obligation yet: it can only be flagged.',
  paymentWith: 'Payment: {{code}}',
  history: {
    title: 'Decisions by validity',
    none: 'No decision recorded.',
    open: '{{from}} onward',
    range: '{{from}} to {{to}}',
    reason: 'Reason: {{reason}}',
    basis: 'Basis: {{basis}}',
    evidence: 'Evidence: {{evidence}}',
  },
  actions: {
    decide: 'Decide',
    decideLabel: 'Decide {{name}}',
    deadline: 'Deadline',
    deadlineLabel: 'Deadline of {{name}}',
    more: 'Actions for {{name}}',
  },
  deadlineRule: {
    column: 'Company rule: {{rule}}',
    from: 'from {{date}}',
    entry: '{{rule}}, {{from}}. Source: {{source}}',
    catalog: 'Catalog rule: {{rule}}. {{source}}',
  },
  deadlineDialog: {
    title: 'Deadline: {{name}}',
    description:
      'Record this company deadline rule when it differs from the catalog, for example the municipal ISS rule. Occurrences not yet transmitted start using the new rule, with its source recorded.',
    listTitle: 'Rules of this company',
    none: 'No rule of its own: the catalog deadline applies.',
    remove: 'Remove',
    removeLabel: 'Remove the rule {{from}}',
    removeError: 'Could not remove the rule. Try again.',
    readOnly: 'Read only: federal obligation deadline rules belong to the head office.',
    readOnlyNoCompanyAccess:
      'Read only: to change this rule, you need access to every establishment of the company.',
    formTitle: 'New rule',
    validFrom: 'Applies to periods ending from',
    validFromHelp: 'Enter a date from 01/01/2027.',
    day: 'Due day',
    dayLast: 'Last day',
    dayOption: 'Day {{day}}',
    businessDay: 'Count business days',
    monthOffset: 'Months after the end of the period',
    monthOffsetHelp: '0 falls due in the period month; 1, in the following month.',
    source: 'Rule source',
    sourcePlaceholder: 'For example: Municipal decree 123/2026, art. 4',
    submit: 'Save deadline',
    close: 'Close',
    saveError: 'Could not save the deadline. Try again.',
  },
  readOnly: {
    notHeadOffice:
      'Federal obligations are decided on the head office. Here they are shown read-only.',
    openHeadOffice: 'Open the head office',
    noTaxpayer:
      'This entity has no federal Taxpayer. Set the Taxpayer in "Basic information" to decide federal obligations; municipal ones can already be decided.',
    noPermission: 'Read only: you do not have permission to decide obligations.',
    row: 'Decided on the head office',
    noCompanyAccess:
      'To decide federal obligations, you need access to every establishment of the company. Ask the person who manages the company record.',
    rowNoCompanyAccess: 'No access to the whole company',
  },
  dialog: {
    title: 'Decide: {{name}}',
    description:
      'The decision applies from the start date and closes the previous one the day before. Earlier decisions stay in the history.',
    stateLabel: 'Decision',
    validFrom: 'Applies from',
    validFromPlaceholder: 'dd/mm/yyyy',
    validFromHelp: 'Decisions apply from 01/01/2027.',
    validFromError: 'Enter a valid date, from 01/01/2027.',
    validTo: 'Applies until',
    validToError: 'Enter a valid date, on or after the start.',
    basisCode: 'Basis code',
    basisDescription: 'Legal basis',
    evidenceCode: 'Evidence code',
    evidenceDescription: 'Evidence',
    reason: 'Reason',
    reasonFlagHelp:
      'Explain why the ERP does not cover this obligation. It shows up as a gap in the obligations center.',
    submit: 'Save decision',
    cancel: 'Cancel',
    saveError: 'Could not save the decision. Try again.',
  },
  errors: {
    OBLIGATION_NOT_FOUND: 'This obligation is not in the catalog. Refresh the page.',
    OBLIGATION_DATE_INVALID: 'Enter valid dates. The end date must be on or after the start date.',
    OBLIGATION_BEFORE_CALENDAR_START: 'Decisions apply from 01/01/2027.',
    OBLIGATION_WAIVER_REQUIRES_BASIS_AND_EVIDENCE:
      'To waive an obligation, enter the legal basis and the evidence, each with a code and a description.',
    OBLIGATION_OUT_OF_SCOPE_REQUIRES_REASON:
      'To flag the obligation as out of scope, enter the reason.',
    OBLIGATION_SCREENING_ONLY_CANNOT_APPLY:
      'The ERP does not cover this obligation yet. Flag it as out of scope, with the reason.',
    OBLIGATION_SCOPE_MISMATCH:
      'Federal obligations are decided on the head office and municipal ones per establishment.',
    OBLIGATION_APPLICABILITY_PERIOD_LOCKED:
      'An obligation already transmitted uses this decision. Pick a start date after the delivered period.',
    OBLIGATION_APPLICABILITY_OVERLAP:
      'A decision already exists for this obligation in the period. Refresh the page and try again.',
    OBLIGATION_RETRY: 'The decisions changed while saving. Refresh the page and try again.',
    OBLIGATION_DEADLINE_RULE_INVALID:
      'Enter the due day (1 to 31, or the last day), the months after the period (0 to 24) and the rule source.',
    OBLIGATION_NO_PERIODS: 'This obligation has no periods, so it has no deadline.',
    OBLIGATION_DEADLINE_OVERRIDE_NOT_FOUND:
      'We could not find this deadline rule. Refresh the page and try again.',
    ESTABLISHMENT_PROFILE_ENTITY_NOT_FOUND:
      'We could not find the establishment. Refresh the page.',
    ESTABLISHMENT_PROFILE_TAXPAYER_NOT_FOUND:
      'We could not find the federal Taxpayer. Refresh the page.',
    ESTABLISHMENT_PROFILE_TAXPAYER_REQUIRED:
      'This entity has no federal Taxpayer. Set it in "Basic information".',
    ESTABLISHMENT_PROFILE_HEAD_OFFICE_ONLY:
      'Federal obligations are decided on the head office. Open the head office to change this.',
    ESTABLISHMENT_PROFILE_ENTITY_SCOPE_FORBIDDEN:
      'This establishment is outside your access scope. Ask an administrator to widen your access.',
  },
};
